// ── Compare API ──────────────────────────────────────────────────────────
(() => {
  const STORAGE_KEY = "compare_products";
  const MAX_ITEMS = 8;

  function get() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      // rimuovi entry invalide che potrebbero essere finite in localStorage
      return raw.filter((i) => i.product_handle && i.variant_id > 0);
    } catch {
      return [];
    }
  }

  function save(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(
      new CustomEvent("compare:updated", { detail: { compare: list } }),
    );
  }

  function add({ product_handle, variant_id }) {
    if (!product_handle || !(variant_id > 0)) return false;
    const list = get();
    if (list.length >= MAX_ITEMS) return false;
    if (!list.some((i) => i.product_handle === product_handle)) {
      list.push({ product_handle, variant_id });
      save(list);
      return true;
    }
    return false;
  }

  function remove(product_handle) {
    save(get().filter((i) => i.product_handle !== product_handle));
  }

  function toggle({ product_handle, variant_id }) {
    has(product_handle)
      ? remove(product_handle)
      : add({ product_handle, variant_id });
  }

  function has(product_handle) {
    return get().some((i) => i.product_handle === product_handle);
  }

  function clear() {
    save([]);
  }

  window.Compare = { get, add, remove, toggle, has, clear };
})();

// ── CompareModal web component ──────────────────────────────────────────────
class CompareModal extends HTMLElement {
  static portalRootId = "compare-portal-root";

  #controller = new AbortController();
  #instance = null;

  connectedCallback() {
    const { signal } = this.#controller;
    document.addEventListener("click", this.#handleClick, { signal });
    window.addEventListener("compare:updated", this.#handleCompareUpdated, {
      signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  static getPortalRoot() {
    let root = document.getElementById(this.portalRootId);
    if (!root) {
      root = document.createElement("div");
      root.id = this.portalRootId;
      document.body.appendChild(root);
    }
    return root;
  }

  get isPortalInstance() {
    return this.dataset.compareInstance === "true";
  }

  #handleClick = (e) => {
    const openBtn = e.target.closest("[data-compare-open]");
    const closeBtn = e.target.closest("[data-compare-close]");
    const removeBtn = e.target.closest("[data-compare-remove]");
    const clearBtn = e.target.closest("[data-compare-clear]");

    if (openBtn) {
      e.preventDefault();
      this.open();
      return;
    }

    if (closeBtn && this.#instance?.contains(closeBtn)) {
      e.preventDefault();
      this.close();
      return;
    }

    if (removeBtn && this.#instance?.contains(removeBtn)) {
      e.preventDefault();
      const handle = removeBtn.dataset.compareRemove;
      if (handle) window.Compare.remove(handle);
    }

    if (clearBtn && this.#instance?.contains(clearBtn)) {
      e.preventDefault();
      window.Compare.clear();
    }
  };

  #handleCompareUpdated = () => {
    if (this.#instance?.classList.contains("is-open")) {
      this.#render();
    }
  };

  open() {
    if (!this.#instance) {
      const portalRoot = CompareModal.getPortalRoot();
      const clone = this.cloneNode(true);
      clone.dataset.compareInstance = "true";
      portalRoot.appendChild(clone);
      this.#instance = clone;
    }

    requestAnimationFrame(() => {
      this.#instance.classList.add("is-open");
      document.body.classList.add("compare-modal-open");
    });

    this.#render();
  }

  close() {
    if (!this.#instance) return;
    this.#instance.classList.remove("is-open");
    document.body.classList.remove("compare-modal-open");
    this.#instance.addEventListener(
      "transitionend",
      () => {
        this.#instance?.remove();
        this.#instance = null;
      },
      { once: true },
    );
  }

  async #render() {
    const grid = this.#instance?.querySelector("[data-compare-grid]");
    if (!grid) return;

    const items = window.Compare.get();

    if (!items.length) {
      this.close();
      return;
    }

    const { i18nLoading, i18nRemove, i18nClose } = this.dataset;

    grid.innerHTML = `<div class="compare-modal__loading" aria-live="polite">${i18nLoading}</div>`;

    const fetches = items.map(({ product_handle, variant_id }) =>
      fetch(
        `/products/${product_handle}?variant=${variant_id}&section_id=compare-product-card`,
      )
        .then((r) => r.text())
        .then((html) => ({ product_handle, html }))
        .catch(() => ({ product_handle, html: null })),
    );

    const results = await Promise.all(fetches);

    grid.innerHTML = "";
    grid.style.gridTemplateColumns = `repeat(${items.length + 1}, 220px)`;

    results.forEach(({ product_handle, html }) => {
      const col = document.createElement("div");
      col.className = "compare-col";
      col.dataset.compareHandle = product_handle;

      const removeBtn = `<button class="compare-col__remove" data-compare-remove="${product_handle}" type="button"><span aria-hidden="true">✕</span> ${i18nRemove}</button>`;

      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const content = doc.querySelector(".compare-card-wrapper");
        col.innerHTML = removeBtn + (content ? content.outerHTML : "");
      } else {
        col.innerHTML = removeBtn + '<p class="compare-col__error">—</p>';
      }

      grid.appendChild(col);
    });

    // Colonna azioni (sempre ultima) — clonata dal template DOM configurabile da tema editor
    const actionsTemplate = document.querySelector('[data-compare-actions-template]');
    const actionCol = /** @type {HTMLElement} */ (
      actionsTemplate ? actionsTemplate.cloneNode(true) : document.createElement('div')
    );
    actionCol.classList.add('compare-col', 'compare-col--actions');
    actionCol.removeAttribute('data-compare-actions-template');

    const closeBtn = document.createElement('button');
    closeBtn.className = 'compare-col__action-btn compare-col__action-btn--close';
    closeBtn.setAttribute('data-compare-close', '');
    closeBtn.type = 'button';
    closeBtn.textContent = i18nClose ?? '';
    actionCol.prepend(closeBtn);

    grid.appendChild(actionCol);
  }
}

if (!customElements.get("compare-modal")) {
  customElements.define("compare-modal", CompareModal);
}

// ── [data-compare-toggle] — click handler ───────────────────────────────────
document.addEventListener(
  "pointerdown",
  (e) => {
    if (e.target.closest("[data-compare-toggle]")) {
      e.preventDefault();
      e.stopPropagation();
    }
  },
  { capture: true },
);

document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-compare-toggle]");
  if (!btn || !window.Compare) return;

  e.preventDefault();
  e.stopPropagation();

  if (btn.dataset.busy === "true") return;

  const product_handle = btn.dataset.productHandle;
  const variant_id = Number(btn.dataset.variantId);
  if (!product_handle || !(variant_id > 0)) return;

  btn.dataset.busy = "true";

  const wasActive = btn.classList.contains("is-active");

  btn.classList.toggle("is-active", !wasActive);
  btn.setAttribute("aria-pressed", String(!wasActive));

  if (wasActive) {
    Compare.remove(product_handle);
  } else {
    const added = Compare.add({ product_handle, variant_id });
    if (!added) {
      // Max raggiunto: revert UI
      btn.classList.remove("is-active");
      btn.setAttribute("aria-pressed", "false");
      window.dispatchEvent(new CustomEvent('compare:max-reached'));
    }
  }

  requestAnimationFrame(() => {
    delete btn.dataset.busy;
  });
});

// ── compare:updated → sincronizza trigger + bottoni ─────────────────────────
window.addEventListener("compare:updated", (e) => {
  const list = e.detail.compare;

  const trigger = document.getElementById("compare-trigger");
  if (trigger) {
    trigger.hidden = list.length === 0;
    const countEl = trigger.querySelector("[data-compare-count]");
    if (countEl) countEl.textContent = list.length;
  }

  document.querySelectorAll("[data-compare-toggle]").forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const isActive = list.some((i) => i.product_handle === handle);
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
});

// ── DOMContentLoaded: stato iniziale ────────────────────────────────────────
window.addEventListener("DOMContentLoaded", () => {
  if (!window.Compare) return;

  const list = Compare.get();

  const trigger = document.getElementById("compare-trigger");
  if (trigger) {
    trigger.hidden = list.length === 0;
    const countEl = trigger.querySelector("[data-compare-count]");
    if (countEl) countEl.textContent = list.length;
  }

  document.querySelectorAll("[data-compare-toggle]").forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const isActive = Compare.has(handle);
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
});

// ── compare:max-reached → toast alert ──────────────────────────────────────
window.addEventListener('compare:max-reached', () => {
  let alert = document.getElementById('compare-max-alert');
  if (!alert) return;

  // Sposta il toast su body la prima volta (nasce nel div hidden di theme.liquid)
  if (alert.parentElement !== document.body) {
    document.body.appendChild(alert);
  }

  clearTimeout(/** @type {any} */ (alert)._hideTimer);
  alert.hidden = false;
  requestAnimationFrame(() => {
    alert.classList.add('is-visible');
    /** @type {any} */ (alert)._hideTimer = setTimeout(() => {
      alert.classList.remove('is-visible');
      alert.addEventListener('transitionend', () => { alert.hidden = true; }, { once: true });
    }, 3000);
  });
});
